import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import InlineAlert from '../../components/InlineAlert';
import { useBilling } from '../../context/useBilling';

const CustomerLookup = () => {
  const navigate = useNavigate();
  const { performLookup, loading, error, clearErrors, resetBillingSession } = useBilling();
  const [formValues, setFormValues] = useState({ accountNumber: '', phoneNumber: '' });

  // Reset entire billing session when arriving at Customer Lookup
  useEffect(() => {
    resetBillingSession();
  }, [resetBillingSession]);

  const handleChange = (e) => {
    const { id, value } = e.target;
    clearErrors();
    setFormValues(v => ({ ...v, [id === 'account-number' ? 'accountNumber' : 'phoneNumber']: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const ok = await performLookup({
      accountNumber: formValues.accountNumber.trim() || undefined,
      phoneNumber: formValues.phoneNumber.trim() || undefined
    });
    if (ok) {
      navigate('../current-bill');
    }
  };

  const handleClear = () => {
    setFormValues({ accountNumber: '', phoneNumber: '' });
    clearErrors();
  };

  return (
    <div className="row billing-panel" role="tabpanel" id="billing-panel-customer-lookup" aria-labelledby="billing-tab-customer-lookup" tabIndex={0} data-testid="billing-panel-customer-lookup">
      <div className="col-lg-8">
        <div className="card border-0 shadow-sm billing-surface-card billing-surface-card--form">
          <div className="card-header bg-light border-0">
            <h5 className="card-title mb-0">
              <i className="bi bi-search me-2 text-tse-primary"></i>
              Find Customer Account
            </h5>
          </div>
          <div className="card-body">
            <form id="customer-lookup-form" onSubmit={handleSubmit} aria-describedby="lookup-instructions" data-testid="form-customer-lookup">
              <fieldset className="border-0 p-0 m-0">
                <legend className="visually-hidden">Search by account number or phone</legend>
              <div id="lookup-instructions" className="visually-hidden">Enter either an account number or a phone number and press Search Account.</div>
              <div className="row">
                <div className="col-md-6 mb-3">
                  <label htmlFor="account-number" className="form-label fw-medium">Account Number</label>
                  <div className="input-group">
                    <span className="input-group-text">
                      <i className="bi bi-person-badge"></i>
                    </span>
                    <input
                      id="account-number"
                      aria-label='Account Number'
                      type="text"
                      className="form-control"
                      placeholder="TSE-123456789"
                      value={formValues.accountNumber}
                      onChange={handleChange}
                      disabled={loading.lookup}
                      inputMode="text"
                      autoComplete="off"
                      data-testid="input-account-number"
                    />
                  </div>
                </div>
                <div className="col-md-6 mb-3">
                  <label htmlFor="phone-number" className="form-label fw-medium">Phone Number</label>
                  <div className="input-group">
                    <span className="input-group-text">
                      <i className="bi bi-telephone"></i>
                    </span>
                    <input
                      id="phone-number"
                      type="tel"
                      aria-label='Phone Number'
                      className="form-control"
                      placeholder="(555) 123-4567"
                      value={formValues.phoneNumber}
                      onChange={handleChange}
                      disabled={loading.lookup}
                      inputMode="tel"
                      autoComplete="tel"
                      data-testid="input-phone-number"
                    />
                  </div>
                </div>
              </div>
              <button type="submit" className="btn btn-tse-primary me-2" disabled={loading.lookup}>
                {loading.lookup ? <><span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>Searching...</> : <><i className="bi bi-search me-2"></i>Search Account</>}
              </button>
              <button type="button" className="btn btn-outline-secondary" onClick={handleClear}>
                <i className="bi bi-arrow-clockwise me-2"></i>Clear
              </button>
              {error && (
                <InlineAlert
                  variant="danger"
                  icon="exclamation-triangle"
                  ariaLabel="Billing account lookup error"
                  message={error}
                  className="mt-3 mb-0 py-2 small"
                />
              )}
              </fieldset>
            </form>
          </div>
        </div>
      </div>
      <div className="col-lg-4">
        <div className="card border-0 shadow-sm billing-surface-card billing-surface-card--aside">
          <div className="card-header bg-light border-0">
            <h6 className="card-title mb-0">
              <i className="bi bi-info-circle me-2 text-info"></i>
              Search Tips
            </h6>
          </div>
          <div className="card-body">
            <div className="small">
              <div className="d-flex align-items-start mb-2">
                <i className="bi bi-check-circle text-success me-2 mt-1 flex-shrink-0"></i>
                <span>Use full account number including TSE prefix</span>
              </div>
              <div className="d-flex align-items-start mb-2">
                <i className="bi bi-check-circle text-success me-2 mt-1 flex-shrink-0"></i>
                <span>Phone number format: (555) 123-4567</span>
              </div>
              <div className="d-flex align-items-start">
                <i className="bi bi-check-circle text-success me-2 mt-1 flex-shrink-0"></i>
                <span>Either field can be used to locate account</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CustomerLookup;
